import pick from 'lodash/pick'
import omit from 'lodash/omit'
import {
    Navigate,
} from 'react-router-dom'

import {
    CommonRoutes,
} from 'app/enums/common.enums'

import {
    SidebarTab,
} from 'app/shared-components/SkyNetDomain'
import {
    ActionBulkComponentFunction,
} from 'app/shared-components/SkyNetDomain/SkyNetDomain.types'
import {
    TableActionsContextProvider,
} from 'app/shared-components/TableActionsContext'
import {
    CustomRouteObjectProps, SkyNetRouteObject,
} from 'app/SkyNetRoutes/SkyNetRoutes.types'
import {
    ActionComponentsKey,
    ActionComponentsType,
    SkyNetObjectConfigParams,
    SkyNetMasterTabConfig, AdditionalRootComponents,
} from './SkyNetObjectRoutes.types'
import SkyNetObjectFormWrapper from './components/SkyNetObjectFormWrapper'
import SkyNetObjectProfileTabs from './components/SkyNetObjectProfileTabs'
import ActionWrapper from './components/ActionWrapper'
import BulkActionWrapper from './components/BulkActionWrapper'

type BulkActionComponentsType = Record<string, {
    component: ActionBulkComponentFunction,
} & CustomRouteObjectProps>

function isFormViewsFn(
    formViews: SidebarTab[] | ((params?: Record<string, any>) => SidebarTab[]),
): formViews is (params?: Record<string, any>) => SidebarTab[] {
    return typeof formViews === 'function'
}

const actionsMap: Record<ActionComponentsKey, string> = {
    [ActionComponentsKey.Create]: 'create',
    [ActionComponentsKey.Print]: 'print',
    [ActionComponentsKey.BulkCreate]: 'bulk-create',
    [ActionComponentsKey.BulkEdit]: 'bulk-edit',
    [ActionComponentsKey.BatchUpdate]: 'batch-update',
    [ActionComponentsKey.CsvUpload]: 'csv-upload',
}

const createActionRoutes = (
    tabs: SkyNetMasterTabConfig<any>[],
    defaultActionComponents: ActionComponentsType,
    uniqField: string,
) => {
    return tabs.map((tab) => {
        const actionComponents = tab.actionComponents || defaultActionComponents

        const actions = pick(actionComponents, [
            ActionComponentsKey.Create,
            ActionComponentsKey.Print,
        ]) as ActionComponentsType

        return Object.entries(actions).map(([
            action,
            {
                component, ...rest
            },
        ]) => {
            return {
                path: `${tab.url}/${actionsMap[action]}/${CommonRoutes.Asterisk}`,
                element: <ActionWrapper
                    component={component}
                    uniqField={uniqField}
                    requestKey={tab.requestKey}
                />,
                ...rest,
            }
        })
    }).flat()
}

const createBulkActionRoute = (actionComponents: ActionComponentsType = {}) => {
    // if we decide to display them in fullscreen,
    // need to move them to the same level as createActionRoutes
    const actions = pick(actionComponents, [
        ActionComponentsKey.BulkCreate,
        ActionComponentsKey.BulkEdit,
    ]) as BulkActionComponentsType

    return Object.entries(actions).map(([
        action,
        {
            component, ...rest
        },
    ]) => {
        return {
            path: `${actionsMap[action]}/${CommonRoutes.Asterisk}`,
            element: (
                <BulkActionWrapper component={component} />
            ),
            ...rest,
        }
    })
}

const createAdditionalRootComponents = (actionComponents: ActionComponentsType = {}) => {
    const actions: AdditionalRootComponents = pick(
        actionComponents,
        [
            ActionComponentsKey.BatchUpdate,
            ActionComponentsKey.CsvUpload,
        ],
    )

    return (
        <>
            {Object.entries(actions).map(([
                key,
                {
                    component: Component,
                },
            ]) => {
                return (
                    <Component key={key} />
                )
            })}
        </>
    )
}

// eslint-disable-next-line import/prefer-default-export
export const createSkyNetObjectRoutes = (
    root: JSX.Element,
    {
        config,
        formViews,
        actionComponents = {},
        defaultTab = 0,
        uniqField = 'id',
    }: SkyNetObjectConfigParams,
    customChildRoutes: SkyNetRouteObject[] = [],
) => {
    return [
        ...config.map((tab) => {
            let forms: SidebarTab[]

            if (tab.formViews) {
                forms = isFormViewsFn(tab.formViews)
                    ? tab.formViews()
                    : tab.formViews
            } else {
                forms = isFormViewsFn(formViews) ? formViews() : formViews || []
            }

            const actions = tab.actionComponents || actionComponents

            const Root = (
                <TableActionsContextProvider>
                    {root}
                    {createAdditionalRootComponents(actions)}
                </TableActionsContextProvider>
            )

            return {
                path: tab.url,
                element: Root,
                ...omit(tab, [
                    'url',
                    'formViews',
                    'actionComponents',
                ]),
                children: [
                    {
                        path: `${CommonRoutes.Edit}/:key/${CommonRoutes.Asterisk}`,
                        element: <SkyNetObjectProfileTabs forms={forms} />,
                        children: [
                            ...forms.map((formView) => {
                                return {
                                    path: formView.url,
                                    element: (
                                        <SkyNetObjectFormWrapper
                                            component={formView.Component}
                                            tabRequestKey={tab.requestKey}
                                        />
                                    ),
                                    ...omit(formView, 'Component'),
                                }
                            }),
                            {
                                path: CommonRoutes.Index,
                                element: (
                                    <Navigate
                                        to={forms[0]?.url || '.'}
                                        replace
                                    />
                                ),
                            },
                        ],
                    },
                    ...createBulkActionRoute(actions),
                ],
            }
        }),
        ...createActionRoutes(config, actionComponents, uniqField),
        ...customChildRoutes,
        {
            path: CommonRoutes.Index,
            element: <Navigate
                to={config[defaultTab]?.url}
                replace
            />,
        },
    ]
}
